import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Copy, Check } from "lucide-react";
import { CONTACT } from "../../data/content";

const Row = ({ label, value, href, testid, copyable = false }) => {
    const [copied, setCopied] = React.useState(false);

    const onCopy = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1400);
        } catch (_) {
            /* clipboard blocked */
        }
    };

    return (
        <a
            data-testid={testid}
            href={href}
            target={href?.startsWith("http") ? "_blank" : undefined}
            rel="noopener noreferrer"
            className="group grid grid-cols-12 gap-3 items-baseline py-4 md:py-5 border-t-2 border-[var(--ink)] hover:bg-[var(--page)] transition-colors px-1 md:px-2"
        >
            <div className="col-span-2 md:col-span-1 mono-meta">
                {label.slice(0, 2)}
            </div>
            <div className="col-span-8 md:col-span-9">
                <div className="mono-label mb-1 text-[var(--ink-soft)]">
                    {label}
                </div>
                <div className="font-serif italic text-xl md:text-3xl tracking-[-0.01em] leading-tight break-all">
                    <span className="link-underline">{value}</span>
                </div>
            </div>
            <div className="col-span-2 flex items-center justify-end gap-2">
                {copyable && (
                    <button
                        onClick={onCopy}
                        aria-label={`Copy ${label}`}
                        className="inline-flex items-center justify-center w-9 h-9 border-hard bg-[var(--surface)] hover:bg-[var(--ink)] hover:text-[var(--page)] transition-colors"
                    >
                        {copied ? (
                            <Check className="w-4 h-4" strokeWidth={2.4} />
                        ) : (
                            <Copy className="w-4 h-4" strokeWidth={2} />
                        )}
                    </button>
                )}
                <span className="inline-flex items-center justify-center w-9 h-9 border-hard bg-[var(--surface)] group-hover:bg-[var(--brand)] group-hover:text-[var(--page)] group-hover:border-[var(--brand)] transition-colors">
                    <ArrowUpRight className="w-4 h-4" strokeWidth={2.2} />
                </span>
            </div>
        </a>
    );
};

export const Contact = () => {
    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative pt-24 md:pt-36 pb-16 md:pb-20"
        >
            <div className="mx-auto max-w-[1400px] px-5 md:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                >
                    <div className="mono-label mb-6 flex items-center gap-2">
                        <span className="inline-block w-2.5 h-2.5 bg-[var(--brand)]" />
                        Ch. 06 — Contact
                    </div>
                    <h2 className="font-serif text-[clamp(2.4rem,8vw,7rem)] leading-[0.9] tracking-[-0.02em]">
                        End of the page.
                        <br />
                        <span className="italic text-[var(--ink-soft)]">
                            Not the conversation
                            <span className="text-[var(--brand)] not-italic">
                                .
                            </span>
                        </span>
                    </h2>
                    <p className="mt-8 md:mt-10 font-sans text-lg md:text-xl text-[var(--ink-soft)] max-w-2xl leading-relaxed">
                        If any of this resonated — a project you&rsquo;re
                        building, a problem you&rsquo;re stuck on, a role you
                        think fits — I&rsquo;d love to hear about it.
                    </p>
                </motion.div>

                <div className="mt-12 md:mt-16 border-b-2 border-[var(--ink)]">
                    <Row
                        label="Email"
                        value={CONTACT.email}
                        href={`mailto:${CONTACT.email}`}
                        testid="contact-email"
                        copyable
                    />
                    <Row
                        label="Phone"
                        value={CONTACT.phone}
                        href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}
                        testid="contact-phone"
                        copyable
                    />
                    <Row
                        label="LinkedIn"
                        value="linkedin.com/in/manasbhansali"
                        href={CONTACT.linkedin}
                        testid="contact-linkedin"
                    />
                    <Row
                        label="GitHub"
                        value="github.com/manasbhansali27"
                        href={CONTACT.github}
                        testid="contact-github"
                    />
                </div>
            </div>
        </section>
    );
};
